import React, { useState } from 'react'
import Container from '@embracesbs/component-container'
import Text from '@embracesbs/component-text'
import Icon from '@embracesbs/component-icon'
import Switch from '@embracesbs/component-switch'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper'
import Button2 from '@embracesbs/component-button2'
import Modal from '@embracesbs/component-modal'

// One entry of the identity-provider modules' additional_oidc_providers list (0.26+). Keys are
// the module's own snake_case names, so the JSON stored in the idp-additional-oidc-providers key
// goes to Terraform as-is. Optional keys left out of an entry get the module's defaults, which is
// why empty optional fields are dropped on save rather than stored as "".
export interface OidcProvider {
  alias_name: string
  client_id: string
  client_secret: string
  authorization_url: string
  token_url: string
  jwks_url: string
  enabled?: boolean
  display_name?: string
  logout_url?: string
  user_info_url?: string
  issuer?: string
  client_default_scopes?: string
  client_authentication_method?: string
  hide_on_login_page?: boolean
  attribute_mappers_enabled?: boolean
  store_tokens?: boolean
  external_userid_claim?: string
  external_upn_claim?: string
  first_name_claim?: string
  last_name_claim?: string
  email_claim?: string
  prompt?: string
  // Fields this editor does not show are kept as they are.
  [key: string]: unknown
}

// Aliases the identity module's _main already creates in every tenant realm; it rejects these.
const RESERVED_ALIASES = [
  'employees',
  'employeeslegacy',
  'helloid',
  'customers-portals-oidc',
  'housing-employees-oidc',
  'malengotest',
  'support',
  'embracesuitedev',
  'master-realm-oidc'
]

// Keycloak identity provider aliases end up in broker URLs, so keep them URL-safe.
const ALIAS_PATTERN = /^[a-z0-9][a-z0-9-]*$/

const REQUIRED_TEXT_FIELDS: { key: keyof OidcProvider; label: string }[] = [
  { key: 'alias_name', label: 'alias' },
  { key: 'client_id', label: 'client ID' },
  { key: 'client_secret', label: 'client secret' },
  { key: 'authorization_url', label: 'authorization URL' },
  { key: 'token_url', label: 'token URL' },
  { key: 'jwks_url', label: 'JWKS URL' }
]

const URL_FIELDS: (keyof OidcProvider)[] = ['authorization_url', 'token_url', 'jwks_url', 'logout_url', 'user_info_url']

const isProvider = (entry: unknown): entry is OidcProvider => typeof entry === 'object' && entry !== null && !Array.isArray(entry)

// The stored value as a list of provider objects; null when it is not one (bad JSON, not a list,
// or an entry that is not an object), so callers can tell "invalid" from "empty".
const parseStrict = (value: string | undefined): OidcProvider[] | null => {
  if (!value?.trim()) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) && parsed.every(isProvider) ? parsed : null
  } catch {
    return null
  }
}

export const parseOidcProviders = (value: string | undefined): OidcProvider[] => parseStrict(value) ?? []

const serialize = (providers: OidcProvider[]): string =>
  JSON.stringify(
    providers.map((provider) =>
      Object.fromEntries(Object.entries(provider).filter(([, fieldValue]) => !(typeof fieldValue === 'string' && fieldValue.trim() === '')))
    )
  )

// The problems that would fail the tenant's Terraform plan, in words an operator can act on.
export const validateOidcProviders = (value: string | undefined): string[] => {
  if (parseStrict(value) === null) {
    return ['the stored value is not a valid list']
  }

  const problems: string[] = []
  const seen = new Set<string>()
  parseOidcProviders(value).forEach((provider, index) => {
    const alias = (provider.alias_name ?? '').trim().toLowerCase()
    const name = alias || `provider ${index + 1}`

    const missing = REQUIRED_TEXT_FIELDS.filter(({ key }) => !String(provider[key] ?? '').trim()).map(({ label }) => label)
    if (missing.length > 0) problems.push(`${name}: fill in ${missing.join(', ')}`)

    if (alias && !ALIAS_PATTERN.test(alias)) problems.push(`${name}: the alias may only contain lowercase letters, digits and dashes`)
    if (RESERVED_ALIASES.includes(alias)) problems.push(`${name}: this alias is already used by a built-in provider`)
    if (alias && seen.has(alias)) problems.push(`${name}: the alias is used more than once`)
    seen.add(alias)

    URL_FIELDS.forEach((key) => {
      const url = String(provider[key] ?? '').trim()
      if (url && !url.startsWith('https://')) problems.push(`${name}: ${String(key).replace(/_/g, ' ')} must start with https://`)
    })
  })
  return problems
}

const emptyProvider = (): OidcProvider => ({
  alias_name: '',
  client_id: '',
  client_secret: '',
  authorization_url: '',
  token_url: '',
  jwks_url: '',
  enabled: true
})

interface AdditionalOidcProvidersProps {
  value: string | undefined
  onChange: (value: string) => void
  isDisabled: boolean
}

const AdditionalOidcProviders: React.FC<AdditionalOidcProvidersProps> = ({ value, onChange, isDisabled }) => {
  const providers = parseOidcProviders(value)
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  const [removing, setRemoving] = useState<number | null>(null)

  const update = (index: number, changes: Partial<OidcProvider>) =>
    onChange(serialize(providers.map((provider, i) => (i === index ? { ...provider, ...changes } : provider))))

  const add = () => onChange(serialize([...providers, emptyProvider()]))

  const confirmRemove = () => {
    if (removing === null) return
    onChange(serialize(providers.filter((_, i) => i !== removing)))
    setExpanded({})
    setRemoving(null)
  }

  const textField = (
    index: number,
    key: keyof OidcProvider,
    label: string,
    options: { isRequired?: boolean; placeholder?: string; flex?: number; type?: 'text' | 'password' } = {}
  ) => (
    <TextFieldWrapper labelText={label} isRequired={options.isRequired} style={{ flex: options.flex ?? 1 }}>
      <TextField
        value={String(providers[index][key] ?? '')}
        isDisabled={isDisabled}
        onChange={(e) => update(index, { [key]: e.target.value })}
        type={options.type ?? 'text'}
        variant='default'
        placeholder={options.placeholder}
      />
    </TextFieldWrapper>
  )

  const switchField = (index: number, key: keyof OidcProvider, label: string, defaultValue: boolean) => (
    <Switch
      name={`oidc-${index}-${String(key)}`}
      label={label}
      onClick={() => update(index, { [key]: !(providers[index][key] ?? defaultValue) })}
      isChecked={Boolean(providers[index][key] ?? defaultValue)}
      isDisabled={isDisabled}
    />
  )

  return (
    <Container isVertical>
      <Text textStyle='description'>
        Extra login options on the tenant realm, next to the employees provider above. Switch one off to hide it and keep its users&apos;
        links. An empty issuer turns off issuer validation, which a multi-tenant upstream needs.
      </Text>

      {providers.map((provider, index) => (
        <Container key={index} isVertical style={{ border: '1px solid var(--tma-border, #e0e0e0)', borderRadius: '8px', padding: '12px' }}>
          <Container style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Text textStyle='h6'>{provider.display_name || provider.alias_name || `Provider ${index + 1}`}</Text>
            <Container style={{ flex: 0, gap: '8px', alignItems: 'center' }}>
              {switchField(index, 'enabled', 'Enabled', true)}
              <Button2
                text='Remove'
                isDanger
                variant='subtle'
                isDisabled={isDisabled}
                leftElement={<Icon iconName='Bin1' size={14} color='var(--tma-danger-text)' />}
                buttonSize='30px'
                buttonRadius='8px'
                type='button'
                onClick={() => setRemoving(index)}
              />
            </Container>
          </Container>
          <Container>
            {textField(index, 'alias_name', 'Alias', { isRequired: true, placeholder: 'e.g. partner-entra' })}
            {textField(index, 'display_name', 'Display name', { placeholder: 'Shown on the login page' })}
          </Container>
          <Container>
            {textField(index, 'client_id', 'Client ID', { isRequired: true })}
            {textField(index, 'client_secret', 'Client secret', { isRequired: true, type: 'password' })}
          </Container>
          <Container>
            {textField(index, 'authorization_url', 'Authorization URL', { isRequired: true, flex: 2 })}
            {textField(index, 'token_url', 'Token URL', { isRequired: true, flex: 2 })}
          </Container>
          <Container>
            {textField(index, 'jwks_url', 'JWKS URL', { isRequired: true, flex: 2 })}
            {textField(index, 'issuer', 'Issuer', { flex: 2, placeholder: 'Empty: no issuer validation' })}
          </Container>
          <Container>
            <Button2
              text={expanded[index] ? 'Hide advanced settings' : 'Advanced settings'}
              variant='subtle'
              leftElement={<Icon iconName={expanded[index] ? 'ArrowUp1' : 'ArrowDown1'} size={14} />}
              buttonSize='30px'
              buttonRadius='8px'
              type='button'
              onClick={() => setExpanded({ ...expanded, [index]: !expanded[index] })}
            />
          </Container>
          {expanded[index] && (
            <Container isVertical>
              <Container>
                {textField(index, 'logout_url', 'Logout URL', { flex: 2 })}
                {textField(index, 'user_info_url', 'User info URL', { flex: 2 })}
              </Container>
              <Container>
                {textField(index, 'client_default_scopes', 'Scopes', { placeholder: 'openid' })}
                {textField(index, 'client_authentication_method', 'Client authentication', { placeholder: 'client_secret_post' })}
                {textField(index, 'prompt', 'Prompt', { placeholder: 'e.g. select_account' })}
              </Container>
              <Container>
                {textField(index, 'external_userid_claim', 'User ID claim', { placeholder: 'oid' })}
                {textField(index, 'external_upn_claim', 'UPN claim', { placeholder: 'upn' })}
                {textField(index, 'email_claim', 'Email claim', { placeholder: 'email' })}
              </Container>
              <Container>
                {textField(index, 'first_name_claim', 'First name claim', { placeholder: 'first_name' })}
                {textField(index, 'last_name_claim', 'Last name claim', { placeholder: 'last_name' })}
              </Container>
              <Container style={{ gap: '16px' }}>
                {switchField(index, 'hide_on_login_page', 'Hide on login page', false)}
                {switchField(index, 'attribute_mappers_enabled', 'Attribute mappers', false)}
                {switchField(index, 'store_tokens', 'Store tokens', true)}
              </Container>
            </Container>
          )}
        </Container>
      ))}

      <Container>
        <Button2
          text='Add identity provider'
          variant='subtle'
          isDisabled={isDisabled}
          leftElement={<Icon iconName='Add' size={14} />}
          buttonSize='30px'
          buttonRadius='8px'
          type='button'
          onClick={add}
        />
      </Container>

      <Modal
        isOpen={removing !== null}
        width='45%'
        height='auto'
        onClose={() => setRemoving(null)}
        onCancel={() => setRemoving(null)}
        hasHeaderBorder={true}
        headerTitle={`Remove ${removing !== null ? providers[removing]?.display_name || providers[removing]?.alias_name || 'provider' : ''}`}
        onConfirm={confirmRemove}
        variant='confirm'
        labelConfirm='Remove'
        labelCancel='Cancel'
      >
        <Text>
          On the next update, Keycloak deletes this provider and every user link made through it, so those users have to link their account
          again when they next log in. To only hide it, switch it off instead.
        </Text>
      </Modal>
    </Container>
  )
}

export default AdditionalOidcProviders
